import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const SessionDetailsModal = ({ session, isOpen, onClose, onTerminate }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !session) return null;

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-success',
      idle: 'text-warning',
      expired: 'text-secondary-500',
      suspicious: 'text-error'
    };
    return colors[status] || colors.expired;
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      low: 'text-success',
      medium: 'text-warning',
      high: 'text-error'
    };
    return colors[riskLevel] || colors.low;
  };

  const mockSecurityEvents = [
    {
      id: 1,
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      event: 'Успешная аутентификация',
      details: 'Пользователь успешно вошел в систему',
      riskLevel: 'low'
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 45 * 60 * 1000),
      event: 'Изменение IP адреса',
      details: 'IP адрес изменился с 192.168.1.44 на 192.168.1.45',
      riskLevel: 'medium'
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 60 * 60 * 1000),
      event: 'Множественные попытки входа',
      details: '3 неудачные попытки входа перед успешной аутентификацией',
      riskLevel: 'high'
    }
  ];

  const mockDeviceFingerprint = {
    userAgent: session.userAgent,
    screenResolution: '1920x1080',
    timezone: 'Europe/Moscow',
    language: 'ru-RU',
    platform: 'Win32',
    cookiesEnabled: true,
    javaEnabled: false,
    plugins: ['Chrome PDF Plugin', 'Chrome PDF Viewer', 'Native Client']
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-xl border border-secondary-200 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-secondary-200">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">
              Детали сессии
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              {session.userName} • {session.deviceType}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-secondary-100 transition-colors duration-150"
          >
            <Icon name="X" size={20} className="text-secondary-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-secondary-200">
          <nav className="flex px-6">
            {[
              { id: 'overview', label: 'Обзор', icon: 'Info' },
              { id: 'security', label: 'Безопасность', icon: 'Shield' },
              { id: 'device', label: 'Устройство', icon: 'Monitor' },
              { id: 'location', label: 'Местоположение', icon: 'MapPin' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors duration-150
                  ${activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                  }
                `}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Session Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-text-primary">Информация о сессии</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">ID сессии:</span>
                      <span className="font-mono text-sm text-text-primary">{session.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Статус:</span>
                      <span className={`font-medium ${getStatusColor(session.status)}`}>
                        {session.status === 'active' ? 'Активна' :
                         session.status === 'idle' ? 'Неактивна' :
                         session.status === 'expired' ? 'Истекла' : 'Подозрительная'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Уровень риска:</span>
                      <span className={`font-medium ${getRiskColor(session.riskLevel)}`}>
                        {session.riskLevel === 'low' ? 'Низкий' :
                         session.riskLevel === 'medium' ? 'Средний' : 'Высокий'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Длительность:</span>
                      <span className="text-text-primary">{session.sessionDuration}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-text-primary">Временные метки</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Время входа:</span>
                      <span className="text-text-primary">{formatTime(session.loginTime)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Последняя активность:</span>
                      <span className="text-text-primary">{formatTime(session.lastActivity)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Actions */}
              <div>
                <h3 className="font-medium text-text-primary mb-3">Выполненные действия</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="flex flex-wrap gap-2">
                    {session.actions.map((action, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700"
                      >
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-text-primary mb-4">События безопасности</h3>
                <div className="space-y-3">
                  {mockSecurityEvents.map(event => (
                    <div key={event.id} className="flex items-start gap-3 p-3 bg-secondary-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        event.riskLevel === 'low' ? 'bg-success' :
                        event.riskLevel === 'medium' ? 'bg-warning' : 'bg-error'
                      }`}></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-text-primary">{event.event}</p>
                          <span className="text-xs text-text-secondary">{formatTime(event.timestamp)}</span>
                        </div>
                        <p className="text-sm text-text-secondary">{event.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-3">Оценка рисков</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="CheckCircle" size={20} className="text-success-600" />
                      </div>
                      <p className="text-sm font-medium text-text-primary">Проверенное устройство</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="AlertTriangle" size={20} className="text-warning-600" />
                      </div>
                      <p className="text-sm font-medium text-text-primary">Новое местоположение</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Icon name="Shield" size={20} className="text-success-600" />
                      </div>
                      <p className="text-sm font-medium text-text-primary">Безопасное соединение</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'device' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-text-primary mb-4">Информация об устройстве</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Тип устройства:</span>
                      <span className="text-text-primary">{session.deviceType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Название:</span>
                      <span className="text-text-primary">{session.deviceName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Разрешение экрана:</span>
                      <span className="text-text-primary">{mockDeviceFingerprint.screenResolution}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Часовой пояс:</span>
                      <span className="text-text-primary">{mockDeviceFingerprint.timezone}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Язык:</span>
                      <span className="text-text-primary">{mockDeviceFingerprint.language}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Платформа:</span>
                      <span className="text-text-primary">{mockDeviceFingerprint.platform}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Cookies:</span>
                      <span className="text-text-primary">
                        {mockDeviceFingerprint.cookiesEnabled ? 'Включены' : 'Отключены'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Java:</span>
                      <span className="text-text-primary">
                        {mockDeviceFingerprint.javaEnabled ? 'Включена' : 'Отключена'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-3">User Agent</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <code className="text-sm text-text-primary break-all">
                    {mockDeviceFingerprint.userAgent}
                  </code>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-3">Установленные плагины</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="space-y-2">
                    {mockDeviceFingerprint.plugins.map((plugin, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Icon name="Puzzle" size={14} className="text-secondary-500" />
                        <span className="text-sm text-text-primary">{plugin}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-text-primary mb-4">Сетевая информация</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">IP адрес:</span>
                      <span className="font-mono text-text-primary">{session.ipAddress}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Местоположение:</span>
                      <span className="text-text-primary">{session.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Координаты:</span>
                      <span className="font-mono text-text-primary">
                        {session.coordinates.lat}, {session.coordinates.lng}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-text-primary mb-4">Карта</h3>
                  <div className="bg-secondary-100 rounded-lg h-48 flex items-center justify-center">
                    <iframe
                      width="100%"
                      height="100%"
                      loading="lazy"
                      title={session.location}
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${session.coordinates.lat},${session.coordinates.lng}&z=14&output=embed`}
                      className="rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-secondary-200 bg-secondary-25">
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Icon name="Clock" size={16} />
            Последнее обновление: {formatTime(new Date())}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-secondary-300 text-text-primary rounded-md hover:bg-secondary-50 transition-colors duration-150"
            >
              Закрыть
            </button>
            <button
              className="px-4 py-2 bg-warning text-white rounded-md hover:bg-warning-600 transition-colors duration-150"
            >
              <Icon name="AlertTriangle" size={16} className="mr-2" />
              Отправить предупреждение
            </button>
            <button
              onClick={onTerminate}
              className="px-4 py-2 bg-error text-white rounded-md hover:bg-error-600 transition-colors duration-150"
            >
              <Icon name="X" size={16} className="mr-2" />
              Завершить сессию
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailsModal;